import { useMutation } from "@apollo/client";
import { useFlashMessages } from "@towersystems/roam-common-native";
import { NativeRemoveBackgroundBase64Result } from "@towersystems/roam-common/lib/generated-types";
import { useCallback } from "react";
import { MUTATION_PREVIEW_REMOVE_BACKGROUND_BASE_64 } from "../../modules/photo-image/components/PhotoCameraPreview/constants";
import { base64ToFile } from "../base-64";
import {
  isRemoveBackgroundBase64Result,
  removalResultToErrorMessage,
} from "./utils";
import { MUTATION_REMOVE_BACKGROUND_BASE_64 } from "./constants";

type RemoveOptions = {
  preview?: boolean;
};

export const useGracefulBackgroundRemoval = () => {
  const { showMessage } = useFlashMessages();

  const [previewRemoveBackgroundBase64] = useMutation<{
    previewRemoveBackgroundBase64: NativeRemoveBackgroundBase64Result;
  }>(MUTATION_PREVIEW_REMOVE_BACKGROUND_BASE_64);

  const [removeBackgroundBase64] = useMutation<{
    removeBackgroundBase64: NativeRemoveBackgroundBase64Result;
  }>(MUTATION_REMOVE_BACKGROUND_BASE_64);

  async function handleRemoveBackground(
    base64: string,
    options: RemoveOptions
  ) {
    if (options.preview) {
      return previewRemoveBackgroundBase64({ variables: { base64 } }).then(
        (d) => {
          return d.data?.previewRemoveBackgroundBase64;
        }
      );
    } else {
      return removeBackgroundBase64({ variables: { base64 } }).then((d) => {
        return d.data?.removeBackgroundBase64;
      });
    }
  }

  const gracefulRemoveBackground = useCallback(
    async (base64: string, name: string, options: RemoveOptions) => {
      try {
        const response = await handleRemoveBackground(base64, options);
        if (!response) {
          throw new Error("Unexpected error removing background");
        }
        if (isRemoveBackgroundBase64Result(response)) {
          const file = base64ToFile(response.base64, `${name}.jpg`);
          return file;
        } else {
          throw new Error(removalResultToErrorMessage(response));
        }
      } catch (e) {
        showMessage({
          message: e instanceof Error ? e.message : "Background removal failed",
          severity: "error",
        });
      }
    },
    []
  );

  return { gracefulRemoveBackground };
};
